<script setup lang="ts">
/**
 * Credential Manage Modal
 * - Add, Edit, Delete credentials for SSM Registry Sync
 * - 등록사이트 영문: L_BASECODE B48(REGISTRY_TYPE) 콤보 선택 → REGISTRY_TYPE 저장, Site URL은 Bigo1 자동 바인딩
 */
import { ref, watch, onMounted, computed } from 'vue';
import { useCredentialStore } from '../../stores/credentialStore';
import type { Credential, CredentialFormData } from '../../types/credential';
import { baseCodeApi } from '@shared/api/baseCodeApi';
import type { BaseCode } from '@shared/types/baseCode.types';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useDialog } from '@/shared/composables/useDialog';
import { EyeIcon, EyeOffIcon, PencilIcon, TrashIcon, PlusIcon } from 'vue-tabler-icons';

const REGISTRY_TYPE_MAIN_CODE = 'B48';
const DEFAULT_COUNTRY = 'MY';

const props = defineProps<{
    modelValue: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'credential-added', credential: Credential): void;
}>();

const credentialStore = useCredentialStore();

// CRS 통합: MYCOID2016(B48001) 폐지 — 계정 관리 목록에서도 숨김 (CRS=B48002만 노출)
const visibleCredentials = computed(() =>
    (credentialStore.credentials || []).filter((c: any) => c.registryType !== 'B48001')
);
const { alert, confirm } = useDialog();

// i18n
const { createSyncT } = useTranslation();
const languageStore = useLanguageStore();
// languageStore.currentLanguage는 이미 computed ref이므로 그대로 사용
const currentLanguage = languageStore.currentLanguage;
const translateRaw = createSyncT(currentLanguage);
const translate = (key: string, fallback: string) => {
    const message = translateRaw(key);
    if (message && message !== key) {
        return message;
    }
    return fallback || key;
};

// 등록사이트 영문 옵션 (L_BASECODE B48)
const registryTypeOptions = ref<BaseCode[]>([]);

// Modal state
const show = computed<boolean>({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value)
});

// Form state
const isFormMode = ref(false);
const editingCredential = ref<Credential | null>(null);
const showPassword = ref(false);
const formValid = ref(false);

const formData = ref<CredentialFormData>({
    alias: '',
    siteUrl: '',
    username: '',
    password: '',
    description: '',
    registryType: ''
});

// Validation rules
const rules = {
    required: (v: string) => !!v || 'This field is required.',
    url: (v: string) => {
        if (!v) return true;
        const pattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;
        return pattern.test(v) || 'Please enter a valid URL.';
    }
};

// B48 등록사이트 영문 옵션 로드 (Display YN = Yes, orderBy 순)
const loadRegistryTypeOptions = async () => {
    try {
        const res = await baseCodeApi.getCodesByMainCode(DEFAULT_COUNTRY, REGISTRY_TYPE_MAIN_CODE);
        // 여러 가능한 응답 구조 지원
        const responseData = res as any;
        const codes = responseData?.data?.codes || 
                     responseData?.data?.data?.codes || 
                     responseData?.codes || 
                     responseData?.data || 
                     [];
        
        if (!Array.isArray(codes) || codes.length === 0) {
            console.warn('No registry type options found (B48)', { responseData });
            registryTypeOptions.value = [];
            return;
        }
        
        registryTypeOptions.value = codes
            .filter((c: any) => {
                const totalCode = c.totalCode || c.TOTAL_CODE || c.total_code || '';
                // CRS 통합: MYCOID2016(B48001) 폐지 — 계정 관리 드롭다운에서 숨김
                if (totalCode === 'B48001') return false;
                const displayYn = c.displayYn ?? c.DISPLAY_YN ?? c.display_yn;
                return displayYn === true || displayYn === 1 || String(displayYn).toUpperCase() === 'TRUE' || String(displayYn).toUpperCase() === 'YES';
            })
            .map((c: any) => ({
                countryCode: c.countryCode || c.COUNTRY_CODE || c.country_code || '',
                totalCode: c.totalCode || c.TOTAL_CODE || c.total_code || '',
                mainCode: c.mainCode || c.MAIN_CODE || c.main_code || '',
                subCode: c.subCode || c.SUB_CODE || c.sub_code || '',
                // CRS 통합: SSM4U(IDPRO) 계정을 "CRS"로 표시
                codeName: (c.totalCode || c.TOTAL_CODE || c.total_code) === 'B48002'
                    ? 'CRS'
                    : (c.codeName || c.CODE_NAME || c.code_name || ''),
                defaultCodeName: c.defaultCodeName || c.DEFAULT_CODE_NAME || c.default_code_name || '',
                language: c.language || c.LANGUAGE || c.language || '',
                systemYn: c.systemYn ?? c.SYSTEM_YN ?? c.system_yn ?? false,
                displayYn: c.displayYn ?? c.DISPLAY_YN ?? c.display_yn ?? false,
                orderBy: c.orderBy ?? c.ORDER_BY ?? c.order_by ?? 0,
                bigo1: c.bigo1 ?? c.BIGO1 ?? c.bigo1 ?? null,
                bigo2: c.bigo2 ?? c.BIGO2 ?? c.bigo2 ?? null,
                bigo3: c.bigo3 ?? c.BIGO3 ?? c.bigo3 ?? null,
                bigo4: c.bigo4 ?? c.BIGO4 ?? c.bigo4 ?? null,
                bigo5: c.bigo5 ?? c.BIGO5 ?? c.bigo5 ?? null,
                bigo6: c.bigo6 ?? c.BIGO6 ?? c.bigo6 ?? null
            } as BaseCode))
            .sort((a, b) => (a.orderBy ?? 0) - (b.orderBy ?? 0));
        
        console.log('Loaded registry type options:', registryTypeOptions.value.length, registryTypeOptions.value);
    } catch (e) {
        console.error('Failed to load registry type options (B48):', e);
        registryTypeOptions.value = [];
    }
};

// 등록사이트 영문 선택 시 Site URL(Bigo1), alias(codeName) 자동 바인딩
const onRegistryTypeChange = (totalCode: string) => {
    const option = registryTypeOptions.value.find((c) => c.totalCode === totalCode);
    if (option) {
        formData.value.siteUrl = option.bigo1?.trim() ?? '';
        formData.value.alias = option.codeName?.trim() ?? '';
    }
};

// REGISTRY_TYPE 코드에 대한 표시명 (목록/편집 시)
// registryTypeOptions가 로드되기 전에도 동작하도록 computed로 처리
const getRegistryTypeDisplayName = (registryType: string | undefined): string => {
    if (!registryType) return '';
    // registryTypeOptions가 아직 로드되지 않았으면 registryType 그대로 반환
    if (registryTypeOptions.value.length === 0) {
        return registryType;
    }
    const option = registryTypeOptions.value.find((c) => c.totalCode === registryType);
    return option?.codeName ?? registryType;
};

// 언어 변경 감지 (languageStore가 반응형이므로 별도 이벤트 리스너 불필요)

// credential 목록이 로드된 후 registryTypeOptions가 비어있으면 다시 로드
watch(() => credentialStore.credentials.length, async (newLength) => {
    if (newLength > 0 && registryTypeOptions.value.length === 0) {
        await loadRegistryTypeOptions();
    }
}, { immediate: true });

// Watch for modal open
watch(() => props.modelValue, async (newVal) => {
    if (newVal) {
        // B48 옵션을 먼저 로드한 후 credential 목록 조회 (표시명 매칭을 위해)
        await loadRegistryTypeOptions();
        await credentialStore.loadCredentials();
        resetForm();
    }
});

// Form actions
const resetForm = () => {
    isFormMode.value = false;
    editingCredential.value = null;
    showPassword.value = false;
    formData.value = {
        alias: '',
        siteUrl: '',
        username: '',
        password: '',
        description: '',
        registryType: ''
    };
};

const startAddMode = async () => {
    resetForm();
    // 옵션이 없으면 다시 로드 시도
    if (registryTypeOptions.value.length === 0) {
        await loadRegistryTypeOptions();
    }
    isFormMode.value = true;
};

const startEditMode = (credential: Credential) => {
    editingCredential.value = credential;
    formData.value = {
        alias: credential.alias,
        siteUrl: credential.siteUrl,
        username: credential.username,
        password: '',
        description: credential.description ?? '',
        registryType: credential.registryType ?? ''
    };
    isFormMode.value = true;
};

const cancelForm = () => {
    resetForm();
};

const saveCredential = async () => {
    if (!formValid.value) return;

    try {
        if (editingCredential.value) {
            const updateData: Partial<CredentialFormData> = {
                alias: formData.value.alias,
                siteUrl: formData.value.siteUrl,
                username: formData.value.username,
                description: formData.value.description,
                registryType: formData.value.registryType || undefined
            };
            if (formData.value.password && formData.value.password.trim() !== '') {
                updateData.password = formData.value.password;
            }
            await credentialStore.updateCredential(editingCredential.value.id, updateData);
        } else {
            const newCredential = await credentialStore.addCredential({
                ...formData.value,
                registryType: formData.value.registryType || undefined
            });
            emit('credential-added', newCredential);
        }
        // 저장 후 목록 새로고침 (registryTypeOptions는 이미 로드되어 있음)
        await credentialStore.loadCredentials();
        resetForm();
    } catch (error: any) {
        console.error('Failed to save credential:', error);

        // REGC001: REGISTRY_TYPE + USER_ID 중복 (이미 등록된 credential)
        if (error?.code === 'REGC001') {
            const registryType = formData.value.registryType || (error as any).registryType;
            const displayName = getRegistryTypeDisplayName(registryType) || registryType || '';
    const duplicateTemplate = translate(
                'entity.Duplicate Credential',
                '"{name}" credential has already been registered.'
            );
            const duplicateMessage = duplicateTemplate.replace('{name}', displayName);

            await alert({
                title: translate('entity.Credential Error', 'Credential Error'),
                message: duplicateMessage,
                type: 'error'
            });
            return;
        }

        const fallbackMessage =
            (error && error.message)
                ? String(error.message)
                : translate('entity.Credential save failed', 'Failed to save credential.');

        await alert({
            title: translate('entity.Credential Error', 'Credential Error'),
            message: fallbackMessage,
            type: 'error'
        });
    }
};

const deleteCredential = async (credential: Credential) => {
    const displayName = getRegistryTypeDisplayName(credential.registryType) || credential.alias;
    const confirmMessage = translate('entity.Delete Credential Confirm', 'Are you sure you want to delete "{name}" credential?').replace('{name}', displayName);
    const confirmed = await confirm({
        title: translate('entity.Credential Error', 'Credential Error'),
        message: confirmMessage,
        type: 'question'
    });
    if (!confirmed) {
        return;
    }
    try {
        await credentialStore.deleteCredential(credential.id);
    } catch (error) {
        console.error('Failed to delete credential:', error);
    }
};

// Format date
const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
    });
};
</script>

<template>
    <v-dialog v-model="show" max-width="700" persistent>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center pa-4">
                <span class="text-h6">
                    {{ isFormMode ? (editingCredential ? translate('entity.Edit Credential', 'Edit Credential') : translate('entity.Register Credential', 'Register Credential')) : translate('entity.Manage Credentials', 'Manage Credentials') }}
                </span>
                <v-btn icon variant="text" @click="show = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text class="pa-4">
                <!-- Form Mode -->
                <template v-if="isFormMode">
                    <v-form v-model="formValid">
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="formData.registryType"
                                    :label="translate('credential.RegistrySite', 'Registry Site') + ' *'"
                                    :items="registryTypeOptions"
                                    item-title="codeName"
                                    item-value="totalCode"
                                    :placeholder="translate('credential.SelectRegistrySite', 'Select a registry site')"
                                    variant="outlined"
                                    density="compact"
                                    :rules="[rules.required]"
                                    @update:model-value="onRegistryTypeChange"
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="formData.alias"
                                    :label="translate('credential.Alias', 'Alias') + ' *'"
                                    :placeholder="translate('credential.AliasPlaceholder', 'Alias (auto-filled from registry site, editable)')"
                                    variant="outlined"
                                    density="compact"
                                    :rules="[rules.required]"
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="formData.siteUrl"
                                    :label="translate('credential.SiteURL', 'Site URL') + ' *'"
                                    :placeholder="translate('credential.SiteURLAutoFill', 'Will be auto-filled when registry site is selected')"
                                    variant="outlined"
                                    density="compact"
                                    :rules="[rules.required, rules.url]"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="formData.username"
                                    :label="translate('credential.Username', 'ID (Username)') + ' *'"
                                    :placeholder="translate('credential.EnterLoginID', 'Enter login ID')"
                                    variant="outlined"
                                    density="compact"
                                    :rules="[rules.required]"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="formData.password"
                                    :label="editingCredential ? translate('credential.PasswordChange', 'Password (enter only if changing)') : translate('credential.Password', 'Password') + ' *'"
                                    :placeholder="editingCredential ? translate('credential.EnterNewPassword', 'Enter new password to change') : translate('credential.EnterPassword', 'Enter password')"
                                    :type="showPassword ? 'text' : 'password'"
                                    variant="outlined"
                                    density="compact"
                                    :rules="editingCredential ? [] : [rules.required]"
                                >
                                    <template v-slot:append-inner>
                                        <v-btn
                                            icon
                                            variant="text"
                                            size="small"
                                            @click="showPassword = !showPassword"
                                        >
                                            <EyeIcon v-if="!showPassword" size="18" />
                                            <EyeOffIcon v-else size="18" />
                                        </v-btn>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="formData.description"
                                    :label="translate('credential.Description', 'Description')"
                                    :placeholder="translate('credential.DescriptionPlaceholder', 'Enter description for this credential (optional)')"
                                    variant="outlined"
                                    density="compact"
                                    rows="3"
                                    auto-grow
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </template>

                <!-- List Mode -->
                <template v-else>
                    <div class="d-flex justify-space-between align-center mb-4">
                        <span class="text-body-2 text-grey">
                            {{ translate('credential.RegisteredCredentials', 'Registered Credentials') }}: {{ credentialStore.credentialCount }}
                        </span>
                        <v-btn
                            color="primary"
                            variant="flat"
                            size="small"
                            @click="startAddMode"
                        >
                            <PlusIcon size="18" class="mr-1" />
                            {{ translate('credential.RegisterNewCredential', 'Register New Credential') }}
                        </v-btn>
                    </div>

                    <v-progress-linear
                        v-if="credentialStore.isLoading"
                        indeterminate
                        color="primary"
                        class="mb-4"
                    />

                    <template v-if="visibleCredentials.length > 0">
                        <v-list lines="two" class="credential-list">
                            <v-list-item
                                v-for="credential in visibleCredentials"
                                :key="credential.id"
                                class="credential-item rounded-md mb-2"
                            >
                                <template v-slot:prepend>
                                    <v-avatar color="primary" size="40">
                                        <v-icon color="white">mdi-key</v-icon>
                                    </v-avatar>
                                </template>

                                <v-list-item-title class="font-weight-medium">
                                    {{ getRegistryTypeDisplayName(credential.registryType) || credential.alias }}
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    <div class="d-flex flex-column gap-1">
                                        <span class="text-caption">
                                            <v-icon size="12" class="mr-1">mdi-web</v-icon>
                                            {{ credential.siteUrl }}
                                        </span>
                                        <span class="text-caption">
                                            <v-icon size="12" class="mr-1">mdi-account</v-icon>
                                            {{ credential.username }}
                                        </span>
                                        <span v-if="credential.description" class="text-caption text-grey">
                                            {{ credential.description }}
                                        </span>
                                        <span class="text-caption text-grey">
                                            {{ translate('credential.Created', 'Created') }}: {{ formatDate(credential.createdAt) }}
                                        </span>
                                    </div>
                                </v-list-item-subtitle>

                                <template v-slot:append>
                                    <div class="d-flex gap-1">
                                        <v-btn
                                            icon
                                            variant="text"
                                            size="small"
                                            color="primary"
                                            @click="startEditMode(credential)"
                                        >
                                            <PencilIcon size="18" />
                                        </v-btn>
                                        <v-btn
                                            icon
                                            variant="text"
                                            size="small"
                                            color="error"
                                            @click="deleteCredential(credential)"
                                        >
                                            <TrashIcon size="18" />
                                        </v-btn>
                                    </div>
                                </template>
                            </v-list-item>
                        </v-list>
                    </template>

                    <template v-else-if="!credentialStore.isLoading">
                        <v-alert type="info" variant="tonal" class="text-center">
                            {{ translate('credential.NoCredentialsRegistered', 'No credentials registered.') }}<br />
                            {{ translate('credential.ClickRegisterButton', 'Click "Register New Credential" button to add one.') }}
                        </v-alert>
                    </template>
                </template>
            </v-card-text>

            <v-divider />

            <v-card-actions class="pa-4">
                <template v-if="isFormMode">
                    <v-btn variant="outlined" @click="cancelForm">
                        {{ translate('credential.Cancel', 'Cancel') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        variant="flat"
                        :disabled="!formValid"
                        :loading="credentialStore.isLoading"
                        @click="saveCredential"
                    >
                        {{ editingCredential ? translate('credential.Update', 'Update') : translate('credential.Register', 'Register') }}
                    </v-btn>
                </template>
                <template v-else>
                    <v-spacer />
                    <v-btn variant="outlined" @click="show = false">
                        {{ translate('credential.Close', 'Close') }}
                    </v-btn>
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.credential-list {
    background: transparent;
}

.credential-item {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    transition: all 0.2s ease;
}

.credential-item:hover {
    border-color: rgb(var(--v-theme-primary));
    background-color: rgba(var(--v-theme-primary), 0.04);
}
</style>
